import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import ImageUploader from '../../components/common/ImageUploader';
import LivePreviewContainer from '../../components/dashboard/LivePreviewContainer';
import { FiSave, FiEye, FiX } from 'react-icons/fi';

const CATEGORIES = [
  'Restaurant',
  'Clothing',
  'Jewellery',
  'Real Estate',
  'Education',
  'Salon',
  'Healthcare',
  'Electronics',
  'Furniture',
  'Travel',
  'Freelancer',
  'Consultant',
  'Other'
];

const BusinessProfilePage = () => {
  const { business, updateBusinessState } = useAuth();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState({
    businessName: '',
    category: 'Other',
    description: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    workingHours: '',
    socialLinks: { instagram: '', facebook: '', youtube: '', linkedin: '' },
    logo: '',
    primaryColor: '#4F46E5',
    secondaryColor: '#06B6D4',
    isPublished: true
  });

  useEffect(() => {
    if (business) {
      setForm({
        businessName: business.businessName || '',
        category: business.category || 'Other',
        description: business.description || '',
        phone: business.phone || '',
        whatsapp: business.whatsapp || '',
        email: business.email || '',
        website: business.website || '',
        address: business.address || '',
        city: business.city || '',
        state: business.state || '',
        country: business.country || 'India',
        workingHours: business.workingHours || 'Mon - Sat: 9:00 AM - 8:00 PM',
        socialLinks: business.socialLinks || { instagram: '', facebook: '', youtube: '', linkedin: '' },
        logo: business.logo || '',
        primaryColor: business.primaryColor || '#4F46E5',
        secondaryColor: business.secondaryColor || '#06B6D4',
        isPublished: business.isPublished !== false
      });
    }

    api.get('/categories').then((res) => setCategories(res.data)).catch(() => {});
    api.get('/products').then((res) => setProducts(res.data)).catch(() => {});
  }, [business]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.businessName || form.businessName.trim().length < 2) {
      showToast('Business name must be at least 2 characters long', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.put('/business', form);
      if (res.success) {
        updateBusinessState(res.data);
        showToast('Business profile updated successfully!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Failed to update business profile', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const liveBusinessObj = {
    ...business,
    ...form
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexDirection: "column", gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 800 }}>
            Business Profile
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
            Edit your business information and see changes reflected in live preview.
          </p>
        </div>

        <button
          onClick={() => setShowMobilePreview(true)}
          className="btn btn-secondary btn-sm"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <FiEye /> Mobile Preview
        </button>
      </div>

      <div className="split-editor-layout">
        {/* EDITOR FORM */}
        <div className="glass-card gcl">
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700 }}>
                Basic Information
              </h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                />
                Published (Visible to Public)
              </label>
            </div>

            <ImageUploader
              label="Business Logo"
              value={form.logo}
              onChange={(url) => setForm({ ...form, logo: url })}
            />

            <div className="form-group">
              <label className="form-label">Business Name *</label>
              <input
                type="text"
                required
                className="form-control"
                value={form.businessName}
                onChange={(e) => setForm({ ...form, businessName: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-control"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Business Description</label>
              <textarea
                rows={4}
                className="form-control"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <hr style={{ borderColor: 'rgba(255,255,255,0.8)', margin: '2rem 0' }} />

            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700, marginBottom: '1.25rem' }}>
              Contact & Location
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-control"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">WhatsApp Number</label>
                <input
                  type="tel"
                  className="form-control"
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Website</label>
                <input
                  type="url"
                  className="form-control"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                type="text"
                className="form-control"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Working Hours</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.workingHours}
                  onChange={(e) => setForm({ ...form, workingHours: e.target.value })}
                />
              </div>
            </div>

            <hr style={{ borderColor: 'rgba(255,255,255,0.8)', margin: '2rem 0' }} />

            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700, marginBottom: '1.25rem' }}>
              Social Media Links
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Instagram URL</label>
                <input
                  type="url"
                  className="form-control"
                  value={form.socialLinks.instagram}
                  onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, instagram: e.target.value } })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Facebook URL</label>
                <input
                  type="url"
                  className="form-control"
                  value={form.socialLinks.facebook}
                  onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, facebook: e.target.value } })}
                />
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: '1.5rem' }}>
              {submitting ? 'Saving Profile...' : 'Save Profile Changes'} <FiSave />
            </button>
          </form>
        </div>

        {/* SIDE BY SIDE LIVE PREVIEW (Desktop) */}
        <LivePreviewContainer
          business={liveBusinessObj}
          categories={categories}
          products={products}
        />
      </div>

      {/* MOBILE PREVIEW MODAL */}
      {showMobilePreview && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '440px', padding: '1rem', background: '#000' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', color: '#fff' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>Live Phone Preview</span>
              <button onClick={() => setShowMobilePreview(false)} style={{ color: '#fff', fontSize: '1.2rem' }}>
                <FiX />
              </button>
            </div>
            <div style={{ borderRadius: '20px', overflow: 'hidden', maxHeight: '75vh', overflowY: 'auto', scrollbarWidth: 'none' }}>
              <LivePreviewContainer business={liveBusinessObj} categories={categories} products={products} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProfilePage;
